import { TableColumnType } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';

import type { PageListColumnVisibilityConfig } from './PageList.interface';

const DEFAULT_ALWAYS_VISIBLE_COLUMN_KEYS = ['action'];

const getColumnKey = (key: React.Key | undefined) => {
  return key === undefined ? undefined : String(key);
};

const getColumnLabel = (column: TableColumnType<any>) => {
  if (typeof column.title === 'string' || typeof column.title === 'number') {
    return String(column.title);
  }

  return getColumnKey(column.key) || '';
};

const getPersistedHiddenColumnKeys = (
  storageKey: string,
  availableColumnKeys: string[],
  alwaysVisibleColumnKeys: string[],
  fallbackHiddenColumnKeys: string[] = []
) => {
  try {
    const storedValue = localStorage.getItem(storageKey);

    if (!storedValue) return fallbackHiddenColumnKeys;

    const parsedValue = JSON.parse(storedValue);

    if (!Array.isArray(parsedValue)) return fallbackHiddenColumnKeys;

    return parsedValue.filter(
      key =>
        typeof key === 'string' &&
        availableColumnKeys.includes(key) &&
        !alwaysVisibleColumnKeys.includes(key)
    );
  } catch {
    return fallbackHiddenColumnKeys;
  }
};

export interface PageListColumnVisibilityOption {
  key: string;
  label: string;
}

export interface PageListColumnVisibilityState<T> {
  isColumnVisibilityEnabled: boolean;
  availableColumns: PageListColumnVisibilityOption[];
  visibleColumns: TableColumnType<T>[];
  hiddenColumnKeys: string[];
  alwaysVisibleColumnKeys: string[];
  handleToggleColumnVisibility: (columnKey: string) => void;
  handleResetColumnVisibility: () => void;
  hasHiddenColumns: boolean;
}

export const usePageListColumnVisibility = <T>(
  columns: TableColumnType<T>[],
  columnVisibilityConfig?: PageListColumnVisibilityConfig
): PageListColumnVisibilityState<T> => {
  const isColumnVisibilityEnabled = Boolean(columnVisibilityConfig?.enabled);
  const excludedColumnKeys = useMemo(
    () => columnVisibilityConfig?.excludedColumnKeys || [],
    [columnVisibilityConfig?.excludedColumnKeys]
  );
  const availableColumns = useMemo(
    () =>
      columns
        .map(column => ({
          key: getColumnKey(column.key),
          label: getColumnLabel(column),
        }))
        .filter(
          (column): column is PageListColumnVisibilityOption =>
            typeof column.key === 'string' &&
            Boolean(column.label) &&
            !excludedColumnKeys.includes(column.key)
        ),
    [columns, excludedColumnKeys]
  );
  const availableColumnKeys = useMemo(
    () => availableColumns.map(column => column.key),
    [availableColumns]
  );
  const alwaysVisibleColumnKeys = useMemo(
    () =>
      columnVisibilityConfig?.alwaysVisibleColumnKeys ||
      DEFAULT_ALWAYS_VISIBLE_COLUMN_KEYS,
    [columnVisibilityConfig?.alwaysVisibleColumnKeys]
  );
  const initiallyHiddenColumnKeys = useMemo(
    () => columnVisibilityConfig?.initiallyHiddenColumnKeys || [],
    [columnVisibilityConfig?.initiallyHiddenColumnKeys]
  );
  const availableInitiallyHiddenColumnKeys = useMemo(
    () =>
      initiallyHiddenColumnKeys.filter(
        key =>
          availableColumnKeys.includes(key) &&
          !alwaysVisibleColumnKeys.includes(key) &&
          !excludedColumnKeys.includes(key)
      ),
    [
      alwaysVisibleColumnKeys,
      availableColumnKeys,
      excludedColumnKeys,
      initiallyHiddenColumnKeys,
    ]
  );
  const shouldPersistColumnVisibility = Boolean(
    columnVisibilityConfig?.persist && columnVisibilityConfig.storageKey
  );
  const [hiddenColumnKeys, setHiddenColumnKeys] = useState<string[]>(() => {
    if (
      !isColumnVisibilityEnabled ||
      (!shouldPersistColumnVisibility &&
        availableInitiallyHiddenColumnKeys.length === 0)
    ) {
      return [];
    }

    if (!shouldPersistColumnVisibility || !columnVisibilityConfig?.storageKey) {
      return availableInitiallyHiddenColumnKeys;
    }

    return getPersistedHiddenColumnKeys(
      columnVisibilityConfig.storageKey,
      availableColumnKeys,
      alwaysVisibleColumnKeys,
      availableInitiallyHiddenColumnKeys
    );
  });

  useEffect(() => {
    if (!isColumnVisibilityEnabled) return;

    setHiddenColumnKeys(prev => {
      const nextHiddenColumnKeys = prev.filter(
        key =>
          availableColumnKeys.includes(key) &&
          !alwaysVisibleColumnKeys.includes(key) &&
          !excludedColumnKeys.includes(key)
      );

      if (
        nextHiddenColumnKeys.length === prev.length &&
        nextHiddenColumnKeys.every((key, index) => key === prev[index])
      ) {
        return prev;
      }

      return nextHiddenColumnKeys;
    });
  }, [
    availableColumnKeys,
    alwaysVisibleColumnKeys,
    excludedColumnKeys,
    isColumnVisibilityEnabled,
  ]);

  useEffect(() => {
    if (
      !isColumnVisibilityEnabled ||
      !shouldPersistColumnVisibility ||
      !columnVisibilityConfig?.storageKey
    ) {
      return;
    }

    if (hiddenColumnKeys.length === 0) {
      if (availableInitiallyHiddenColumnKeys.length > 0) {
        localStorage.setItem(columnVisibilityConfig.storageKey, '[]');
        return;
      }

      localStorage.removeItem(columnVisibilityConfig.storageKey);
      return;
    }

    localStorage.setItem(
      columnVisibilityConfig.storageKey,
      JSON.stringify(hiddenColumnKeys)
    );
  }, [
    columnVisibilityConfig?.storageKey,
    availableInitiallyHiddenColumnKeys.length,
    hiddenColumnKeys,
    isColumnVisibilityEnabled,
    shouldPersistColumnVisibility,
  ]);

  const visibleColumns = useMemo(() => {
    if (!isColumnVisibilityEnabled) return columns;

    return columns.filter(column => {
      const columnKey = getColumnKey(column.key);

      if (!columnKey) return true;
      if (excludedColumnKeys.includes(columnKey)) return false;
      if (alwaysVisibleColumnKeys.includes(columnKey)) return true;

      return !hiddenColumnKeys.includes(columnKey);
    });
  }, [
    alwaysVisibleColumnKeys,
    columns,
    excludedColumnKeys,
    hiddenColumnKeys,
    isColumnVisibilityEnabled,
  ]);

  const handleToggleColumnVisibility = (columnKey: string) => {
    if (alwaysVisibleColumnKeys.includes(columnKey)) return;
    if (excludedColumnKeys.includes(columnKey)) return;

    setHiddenColumnKeys(prev => {
      if (prev.includes(columnKey)) {
        return prev.filter(key => key !== columnKey);
      }

      return [...prev, columnKey];
    });
  };

  const handleResetColumnVisibility = () => {
    setHiddenColumnKeys(availableInitiallyHiddenColumnKeys);

    if (shouldPersistColumnVisibility && columnVisibilityConfig?.storageKey) {
      localStorage.removeItem(columnVisibilityConfig.storageKey);
    }
  };

  return {
    isColumnVisibilityEnabled,
    availableColumns,
    visibleColumns,
    hiddenColumnKeys,
    alwaysVisibleColumnKeys,
    handleToggleColumnVisibility,
    handleResetColumnVisibility,
    hasHiddenColumns: hiddenColumnKeys.length > 0,
  };
};
